import { store } from '../store';
import { api } from '../http';
import { DefaultRes } from '../types';
import { AxiosError, AxiosResponse } from 'axios';
import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { Group } from '../types/groups.types';
import { Team } from '../types/teams.types';
import type { L1Group, L1Stats, L2Team, L2Stats } from '../store/groups-teams.store';
import { TagsService } from './tags-service';
import { getEffectiveSetting, SETTING } from '../hooks/use-effective-settings';
import { stripCheckOnMeLegacyFields } from '../utils/checkOnMeLegacyFields';

type Callback = (isValid?: boolean, message?: string) => void;

// Cache for the full groups data to avoid refetching on every navigation
let _groupsCache: Group[] | null = null;
let _usersCache: any[] | null = null;
let _systemUsersCache: any[] | null = null;
let _groupsCacheTs = 0;
let _usersCacheTs = 0;
let _systemUsersCacheTs = 0;
const CACHE_TTL_MS = 30_000; // 30 seconds

// Invalidate on logout, session expiry, and masquerade switch
const _clearModuleCache = () => {
    _groupsCache = null;
    _usersCache = null;
    _systemUsersCache = null;
    _groupsCacheTs = _usersCacheTs = _systemUsersCacheTs = 0;
};
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('sessionExpired', _clearModuleCache);
}

const _readGroupsTeamsSlice = () => {
    if (typeof store?.getState !== 'function') return undefined;
    return (store.getState() as any).groupsTeams;
};
// Clear cache when stores are reset (logout / masquerade switch)
let _prevGroupsTeams = _readGroupsTeamsSlice();
if (typeof store?.subscribe === 'function') {
    store.subscribe(() => {
        const gt = _readGroupsTeamsSlice();
        // Detect RESET_ALL_STORES: groupsTeams object reference changes to initial state
        if (_prevGroupsTeams && gt !== _prevGroupsTeams && !gt?.l1Groups?.length && !gt?.l2Group) {
            _clearModuleCache();
        }
        _prevGroupsTeams = gt;
    });
}

export class GroupsTeamsService {
    public static readonly dispatch = store.dispatch.groupsTeams;

    // ── Data loading (uses existing deployed endpoints) ─────

    /** Load all groups+teams+users from the existing endpoint */
    private static async loadGroupsData(): Promise<Group[]> {
        if (_groupsCache && Date.now() - _groupsCacheTs < CACHE_TTL_MS) return _groupsCache;
        const { data: { result, isValid } } = await api.get<
            DefaultRes<Group[]>,
            AxiosResponse<DefaultRes<Group[]>>
        >('groups/fetch-all-groups-teams-users');
        if (isValid && Array.isArray(result)) {
            _groupsCache = result;
            _groupsCacheTs = Date.now();
            return result;
        }
        return [];
    }

    /** Load all POM users from the existing endpoint */
    private static async loadUsersData(): Promise<any[]> {
        if (_usersCache && Date.now() - _usersCacheTs < CACHE_TTL_MS) return _usersCache;
        const { data: { result, isValid } } = await api.get<
            DefaultRes<any[]>,
            AxiosResponse<DefaultRes<any[]>>
        >('users/getSubscriptionUsers');
        if (isValid && Array.isArray(result)) {
            _usersCache = result;
            _usersCacheTs = Date.now();
            return result;
        }
        return [];
    }

    /** Load system users (customerWebAppUser) with caching */
    private static async loadSystemUsers(): Promise<any[]> {
        if (_systemUsersCache && Date.now() - _systemUsersCacheTs < CACHE_TTL_MS) return _systemUsersCache;
        try {
            const { data: { result, isValid } } = await api.get<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`/customerWebAppUser?${new URLSearchParams({ perPage: '200', page: '1' })}`);
            if (isValid && result?.items) {
                _systemUsersCache = result.items;
                _systemUsersCacheTs = Date.now();
                return result.items;
            }
        } catch { /* non-critical */ }
        return _systemUsersCache || [];
    }

    /** Invalidate caches (call after CRUD mutations) */
    private static invalidateCache() {
        _clearModuleCache();
    }

    // ── L1: Overview ──────────────────────────────────────

    public static async fetchOverview(callback?: Callback) {
        const { toggleLoading, setOverview } = GroupsTeamsService.dispatch;
        toggleLoading('overview', true);
        try {
            // The overview endpoint returns tags resolved to names, leaders
            // populated server-side (tolerating string- and ObjectId-stored
            // leadersIds), and precomputed stats — no client-side joins.
            const { data: { result, isValid } } = await api.get<
                DefaultRes<{ groups: any[]; stats: L1Stats }>,
                AxiosResponse<DefaultRes<{ groups: any[]; stats: L1Stats }>>
            >('groups-teams/overview');

            if (!isValid || !result) {
                toggleLoading('overview', false);
                callback?.(false, 'An error occurred. Please try again.');
                return;
            }

            const l1Groups: L1Group[] = (result.groups || []).map((g: any) => ({
                id: g.id,
                name: g.name,
                location: g.location,
                tags: g.tags || [],
                teamCount: g.teamCount || 0,
                memberCount: g.memberCount || 0,
                teamIds: g.teamIds || [],
                leadersIds: g.leadersIds,
                leaders: g.leaders || [],
                subscribersIds: g.subscribersIds,
                settings: g.settings || {},
            }));

            setOverview({
                groups: l1Groups,
                stats: result.stats ?? { groups: 0, teams: 0, pomUsers: 0, unassigned: 0 },
            });
            toggleLoading('overview', false);
            callback?.(true, undefined);
        } catch (error: any) {
            toggleLoading('overview', false);
            handleError(error, callback);
        }
    }

    // ── L2: Group Detail ──────────────────────────────────

    public static async fetchGroupDetail(groupId: string, callback?: Callback) {
        const { toggleLoading, setGroupDetail } = GroupsTeamsService.dispatch;
        toggleLoading('groupDetail', true);
        try {
            const groups = await GroupsTeamsService.loadGroupsData();
            const group = groups.find((g) => g.id === groupId);

            if (!group) {
                toggleLoading('groupDetail', false);
                callback?.(false, 'Group not found');
                return;
            }

            // Load tags to resolve IDs → names
            let tagMap = new Map<string, string>();
            try {
                const allTags = await TagsService.fetchAll();
                if (allTags) {
                    (allTags as any[]).forEach((t) => tagMap.set(t._id || t.id, t.name));
                }
            } catch { /* non-critical */ }

            // NOTE: falls back to the raw id when a tag can't be named. That id can
            // reach the screen via TagPills, but it must NOT be dropped here:
            // TagExplorerPage and GroupsTeamsPage consume these arrays as DATA and
            // do their own id-or-name resolution, so discarding the id turns a
            // swallowed tag-fetch failure into "this group has no tags" and zeroes
            // the Tag Explorer counts. Closing this leak properly means returning
            // {id, name} pairs and resolving at render — tracked as a follow-up.
            const resolveTagNames = (ids: string[]) =>
                // allow-raw-id-fallback: these arrays are consumed as DATA (TagExplorer
                // resolves id-or-name itself), so dropping the id loses information.
                (ids || []).map((id) => tagMap.get(id) || id).filter(Boolean);

            // Fetch system users once — used for both group leaders and team leaders
            const systemUsers = await GroupsTeamsService.searchSystemUsers('', 0);

            const resolveLeader = (lid: string) => {
                const u = systemUsers.find((su: any) => su.id === lid);
                return u
                    ? { id: u.id, fullname: u.fullname, firstName: u.fullname?.split(' ')[0], lastName: u.fullname?.split(' ').slice(1).join(' '), email: u.email }
                    : { id: lid };
            };

            // Filter out ghost team entries (from aggregation $unwind with preserveNullAndEmptyArrays)
            const validTeams = (group.teams || []).filter((t: any) => t.id || t._id);
            const teams: L2Team[] = validTeams.map((t: any) => ({
                id: t.id,
                name: t.name,
                location: t.location,
                tags: resolveTagNames(t.tags || []),
                memberCount: t.membersIds?.length || t.users?.length || 0,
                membersIds: t.membersIds,
                leadersIds: t.leadersIds,
                leaders: (t.leadersIds || []).map((lid: string) => resolveLeader(lid)),
                settings: t.settings || {},
                subscribersIds: [],
            }));

            const totalMembers = teams.reduce((sum, t) => sum + t.memberCount, 0);

            // No "unassigned to team" stat here: a POM user belongs to a group only
            // through a team (User.teamId → Team.groupId), so a user without a team
            // has no group and the count is structurally always 0. The old card
            // showed a hard-coded 0 next to the real org-wide "Unassigned Users"
            // badge on the overview and read as a third, contradictory number
            // (ticket 868m9kpcm).
            const stats: L2Stats = {
                teams: teams.length,
                members: totalMembers,
                groupLeaders: (group.leadersIds || []).length,
            };

            // Resolve group leaders from the same system users
            const groupLeaders = (group.leadersIds || []).map((lid: string) => resolveLeader(lid));

            setGroupDetail({
                group: {
                    id: group.id,
                    name: group.name,
                    location: group.location,
                    tags: group.tags || [],
                    teamIds: group.teamIds || validTeams.map((t: any) => t.id),
                    settings: group.settings || {},
                    subscribersIds: group.subscribersIds,
                    leadersIds: group.leadersIds,
                    groupLeaders,
                },
                teams,
                stats,
            });
            toggleLoading('groupDetail', false);
            callback?.(true, undefined);
        } catch (error: any) {
            toggleLoading('groupDetail', false);
            handleError(error, callback);
        }
    }

    // ── L3: Team Detail ───────────────────────────────────

    public static async fetchTeamDetail(
        groupId: string,
        teamId: string,
        page = 0,
        pageSize = 5,
        callback?: Callback
    ) {
        const { toggleLoading, setTeamDetail } = GroupsTeamsService.dispatch;
        toggleLoading('teamDetail', true);
        try {
            const [groups, allUsers] = await Promise.all([
                GroupsTeamsService.loadGroupsData(),
                GroupsTeamsService.loadUsersData(),
            ]);
            const group = groups.find((g) => g.id === groupId);
            const team = group?.teams?.find((t) => t.id === teamId)
                || groups.flatMap((g) => g.teams || []).find((t) => t.id === teamId);

            if (!team) {
                toggleLoading('teamDetail', false);
                callback?.(false, 'Team not found');
                return;
            }
            // membersIds may be empty while users (populated objects) has the data
            const memberIds = (team.membersIds?.length ? team.membersIds : (team.users || []).map((u: any) => u.id || u._id).filter(Boolean)) as string[];
            const allMembers = memberIds
                .map((mid) => allUsers.find((u) => u.id === mid || u._id === mid))
                .filter(Boolean);

            // Send all members — let the card handle pagination + search
            const total = allMembers.length;

            // Resolve leaders from system users (CustomerWebAppUser)
            let leaders: any[] = [];
            if (team.leadersIds && team.leadersIds.length > 0) {
                const systemUsers = await GroupsTeamsService.searchSystemUsers('', 0);
                leaders = systemUsers
                    .filter((u: any) => team.leadersIds!.includes(u.id))
                    .map((u: any) => ({
                        id: u.id,
                        fullname: u.fullname,
                        firstName: u.fullname?.split(' ')[0],
                        lastName: u.fullname?.split(' ').slice(1).join(' '),
                        email: u.email,
                    }));
            }

            const parentGroup = group
                ? { id: group.id, name: group.name }
                : null;

            setTeamDetail({
                team: {
                    ...team,
                    settings: team.settings || {},
                },
                members: allMembers,
                leaders,
                parentGroup,
                pagination: {
                    page,
                    pageSize,
                    total,
                    totalPages: Math.ceil(total / pageSize),
                },
            });
            toggleLoading('teamDetail', false);
            callback?.(true, undefined);
        } catch (error: any) {
            toggleLoading('teamDetail', false);
            handleError(error, callback);
        }
    }

    /** Fetch a different page of members without reloading entire team detail */
    public static async fetchTeamMembers(
        groupId: string,
        teamId: string,
        page: number,
        pageSize = 5,
        callback?: Callback
    ) {
        // Just re-fetch the full team detail with the new page
        await GroupsTeamsService.fetchTeamDetail(groupId, teamId, page, pageSize, callback);
    }

    // ── Search ────────────────────────────────────────────

    public static async search(query: string, callback?: Callback) {
        const { toggleLoading, setSearchResults } = GroupsTeamsService.dispatch;
        toggleLoading('search', true);
        try {
            const [groups, allUsers] = await Promise.all([
                GroupsTeamsService.loadGroupsData(),
                GroupsTeamsService.loadUsersData(),
            ]);

            const q = query.toLowerCase().trim();
            if (!q) {
                setSearchResults({ people: [], teams: [], groups: [] });
                toggleLoading('search', false);
                callback?.(true, undefined);
                return;
            }

            // Search people
            const people = allUsers
                .filter((u) =>
                    `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(q) ||
                    (u.phoneNumber || '').includes(q) ||
                    (u.employeeId || '').toLowerCase().includes(q)
                )
                .slice(0, 10)
                .map((u) => {
                    // Find what team/group this user belongs to
                    let teamName = '';
                    let groupName = '';
                    let groupId = '';
                    if (u.teamId) {
                        for (const g of groups) {
                            const t = (g.teams || []).find((t) => t.id === u.teamId);
                            if (t) {
                                teamName = t.name || '';
                                groupName = g.name;
                                groupId = g.id;
                                break;
                            }
                        }
                    }
                    return {
                        id: u.id || u._id,
                        firstName: u.firstName,
                        lastName: u.lastName,
                        phoneNumber: u.phoneNumber,
                        employeeId: u.employeeId,
                        teamId: u.teamId,
                        teamName,
                        groupId,
                        groupName,
                    };
                });

            // Search teams
            const teams = groups
                .flatMap((g) =>
                    (g.teams || [])
                        .filter((t) => (t.name || '').toLowerCase().includes(q))
                        .map((t) => ({
                            id: t.id,
                            name: t.name || '',
                            membersIds: t.membersIds || [],
                            groupId: g.id,
                            groupName: g.name,
                        }))
                )
                .slice(0, 10);

            // Search groups
            const matchedGroups = groups
                .filter((g) => (g.name || '').toLowerCase().includes(q))
                .slice(0, 10)
                .map((g) => ({
                    id: g.id,
                    name: g.name,
                    teamCount: (g.teams || []).filter((t: any) => t.id || t._id).length,
                }));

            setSearchResults({ people, teams, groups: matchedGroups });
            toggleLoading('search', false);
            callback?.(true, undefined);
        } catch (error: any) {
            toggleLoading('search', false);
            handleError(error, callback);
        }
    }

    // ── CRUD operations (native pom-2-api endpoints) ──────

    public static async createGroup(
        body: { name: string; location?: string; leadersIds?: string[]; tags?: string[] },
        callback?: Callback
    ) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >('groups/create-group', body);
            toggleLoading('saving', false);
            if (isValid) {
                GroupsTeamsService.invalidateCache();
                await GroupsTeamsService.fetchOverview();
            }
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async updateGroup(groupId: string, body: Record<string, any>, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`groups/update-group/${groupId}`, body);
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            if (isValid) await GroupsTeamsService.fetchGroupDetail(groupId);
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async deleteGroup(groupId: string, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`groups/delete-group/${groupId}`, {});
            toggleLoading('saving', false);
            if (isValid) {
                GroupsTeamsService.invalidateCache();
                await GroupsTeamsService.fetchOverview();
            }
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async createTeam(body: Record<string, any>, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >('teams/create-team', body);
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async updateTeam(teamId: string, body: Record<string, any>, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`teams/update-team/${teamId}`, body);
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async deleteTeam(teamId: string, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`teams/delete-team/${teamId}`, {});
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async addMember(
        teamId: string,
        userId: string,
        callback?: Callback,
        userData?: { firstName?: string; lastName?: string; phoneNumber?: string; employeeId?: string }
    ) {
        if (!teamId || !/^[0-9a-fA-F]{24}$/.test(teamId)) {
            callback?.(false, 'Invalid team ID');
            return;
        }
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            // Existing user: reassign to team via users/addUser with userId
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >('users/addUser', {
                payload: {
                    firstName: userData?.firstName || '',
                    lastName: userData?.lastName || '',
                    phoneNumber: userData?.phoneNumber || null,
                    employeeId: userData?.employeeId || '',
                    teamId,
                },
                uniqueId: await getEffectiveSetting(SETTING.userUniqueIdentifier, 'INVITECODE'),
                userId,
            });
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, message);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async removeMember(teamId: string, userId: string, groupId?: string, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            // Build full team payload from store — update-team requires name + groupId
            const state = store.getState().groupsTeams as any;
            const team = state.l3?.team;
            if (!team) {
                toggleLoading('saving', false);
                callback?.(false, 'Team data not loaded');
                return;
            }
            const resolvedGroupId = groupId || team.groupId || state.l3?.parentGroup?.id;
            if (!resolvedGroupId) {
                toggleLoading('saving', false);
                callback?.(false, 'Cannot determine group for this team');
                return;
            }
            const currentMembers: string[] = (team.membersIds?.length ? team.membersIds : null)
                || state.l3?.members?.map((m: any) => m.id || m._id) || [];
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`teams/update-team/${teamId}`, {
                name: team.name,
                groupId: resolvedGroupId,
                membersIds: currentMembers.filter((id: string) => id !== userId),
                leadersIds: team.leadersIds || state.l3?.leaders?.map((l: any) => l.id) || [],
                tags: team.tags || [],
                // Don't echo the legacy Check On Me fields back on an
                // unrelated member removal — pom-2-api's dual-write would
                // read an unchanged legacy value as a real edit.
                settings: stripCheckOnMeLegacyFields(team.settings),
            });
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    // ── Team Settings (Check On Me) ───────────────────────
    // NOTE: These endpoints are new in pom-2-api — will work once deployed

    public static async getTeamSettings(teamId: string, callback?: Callback) {
        try {
            const { data: { result, isValid, message } } = await api.get<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`groups-teams/team-settings/${teamId}`);
            callback?.(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            handleError(error, callback);
            return null;
        }
    }

    public static async updateTeamSettings(
        teamId: string,
        settings: { checkOnMeEnabled?: boolean; escalationBehavior?: string },
        callback?: Callback
    ) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.put<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`groups-teams/team-settings/${teamId}`, settings);
            toggleLoading('saving', false);
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async resetTeamSettings(teamId: string, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`groups-teams/team-settings/${teamId}/reset`, {});
            toggleLoading('saving', false);
            GroupsTeamsService.invalidateCache();
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }

    public static async fetchUnassignedUsers(
        page = 0,
        pageSize = 20,
        search = ''
    ): Promise<{ users: any[]; total: number }> {
        try {
            const allUsers = await GroupsTeamsService.loadUsersData();
            let unassigned = allUsers.filter((u) => !u.teamId);

            // Apply search filter
            if (search.trim()) {
                const q = search.toLowerCase();
                unassigned = unassigned.filter((u) =>
                    `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(q) ||
                    (u.phoneNumber || '').includes(q) ||
                    (u.employeeId || '').toLowerCase().includes(q)
                );
            }

            const total = unassigned.length;
            const start = page * pageSize;
            const users = unassigned.slice(start, start + pageSize).map((u) => ({
                id: u.id || u._id,
                firstName: u.firstName,
                lastName: u.lastName,
                phoneNumber: u.phoneNumber,
                employeeId: u.employeeId,
            }));

            return { users, total };
        } catch {
            return { users: [], total: 0 };
        }
    }

    public static async searchSystemUsers(query: string, limit = 50, skip = 0): Promise<any[]> {
        try {
            const { data: { result, isValid } } = await api.get<
                DefaultRes<any[]>,
                AxiosResponse<DefaultRes<any[]>>
            >('groups-teams/system-users', {
                params: { q: query || undefined, limit: limit || undefined, skip: skip || undefined },
            });
            if (isValid && Array.isArray(result)) {
                return result.map((u: any) => ({
                    id: u.id,
                    fullname: u.fullname || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                    email: u.email,
                }));
            }
        } catch { /* non-critical */ }
        return [];
    }

    // ── Alert Subscriptions ───────────────────────────────

    public static async loadMySubscriptions() {
        const { setMySubscriptions } = GroupsTeamsService.dispatch;
        try {
            const { data: { result, isValid } } = await api.get<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >('alert-subscriptions/my-subscriptions');
            if (isValid && Array.isArray(result)) {
                const keys = new Set(result.map((s: any) => `${s.targetType}:${s.targetId}`));
                setMySubscriptions(keys);
            }
        } catch {
            // silent — subscriptions are non-critical
        }
    }

    /**
     * `related` lets the caller name the keys a successful unsubscribe also
     * invalidates on the server: a team's parent group (the server demotes the
     * group subscription to the sibling teams) or a group's teams (the server
     * clears the legacy per-team rows). They are dropped optimistically so the
     * UI never keeps showing "Alerts On" if the follow-up re-read fails.
     */
    public static async toggleSubscription(
        targetType: 'group' | 'team',
        targetId: string,
        currentlyWatching: boolean,
        callback?: Callback,
        related?: { groupId?: string | null; teamIds?: ReadonlyArray<string> }
    ) {
        const { addSubscription, removeSubscription } = GroupsTeamsService.dispatch;

        // Group-level: one subscription on the group itself. Alert routing reads
        // Group.subscribersIds for every team under the group, so this also
        // covers teams created after the subscribe (fanning out per team did
        // not). The server clears any per-team rows under the group on
        // unsubscribe, so the key set is re-read from it afterwards.
        if (targetType === 'group') {
            const groupKey = `group:${targetId}`;
            try {
                const endpoint = currentlyWatching
                    ? 'alert-subscriptions/unsubscribe'
                    : 'alert-subscriptions/subscribe';
                const { data: { isValid, message } } = await api.post<
                    DefaultRes<any>,
                    AxiosResponse<DefaultRes<any>>
                >(endpoint, { targetType, targetId });
                if (isValid) {
                    if (currentlyWatching) {
                        removeSubscription(groupKey);
                        (related?.teamIds || []).forEach((tid) => removeSubscription(`team:${tid}`));
                    } else {
                        addSubscription(groupKey);
                    }
                    await GroupsTeamsService.loadMySubscriptions();
                }
                callback?.(isValid, message);
            } catch (error: any) {
                handleError(error, callback);
            }
            return;
        }

        // Team-level: single subscribe/unsubscribe
        const key = `team:${targetId}`;
        try {
            const endpoint = currentlyWatching
                ? 'alert-subscriptions/unsubscribe'
                : 'alert-subscriptions/subscribe';
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(endpoint, { targetType, targetId });
            if (isValid) {
                if (currentlyWatching) {
                    removeSubscription(key);
                    // Unsubscribing from one team while watching its group
                    // converts the group subscription into per-team ones on
                    // the server; drop the group key now and re-read the rest.
                    if (related?.groupId) removeSubscription(`group:${related.groupId}`);
                } else {
                    addSubscription(key);
                }
                await GroupsTeamsService.loadMySubscriptions();
            }
            callback?.(isValid, message);
        } catch (error: any) {
            handleError(error, callback);
        }
    }

    public static async deleteTag(tagId: string, callback?: Callback) {
        const { toggleLoading } = GroupsTeamsService.dispatch;
        toggleLoading('saving', true);
        try {
            const { data: { isValid, message } } = await api.post<
                DefaultRes<any>,
                AxiosResponse<DefaultRes<any>>
            >(`tags/delete-tag/${tagId}`, {});
            toggleLoading('saving', false);
            callback?.(isValid, RESPONSE_CODES[message]);
        } catch (error: any) {
            toggleLoading('saving', false);
            handleError(error, callback);
        }
    }
}

function handleError(error: any, callback?: Callback) {
    if (error.response) {
        const code = (error as AxiosError<DefaultRes>).response?.data?.message;
        callback?.(false, RESPONSE_CODES[code!] || code || 'An error occurred');
        return;
    }
    console.error('[GroupsTeamsService]', error);
    callback?.(false, 'An error occurred. Please try again.');
}
